<template>
  <div
    class="accordion"
    :class="{ 'accordion--open': isOpenInternal }"
    :style="cssVars"
  >
    <div
      class="accordion__header"
      role="button"
      :aria-expanded="isOpenInternal"
      @click="toggle"
    >
      <div class="accordion__header-content">
        <slot name="header" />
        <div class="accordion__icon">
          <MIcon
            :icon="bulletIcon"
          />
        </div>
      </div>

      <div
        v-if="needCloseIcon"
        class="accordion__cross-icon"
        @click.stop="handleClose"
      >
        <MIcon
          :icon="smoothCrossIcon"
        />
      </div>
    </div>

    <transition
      name="accordion-content"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-show="isOpenInternal"
        ref="content"
        class="accordion__content"
      >
        <div class="accordion__content-inner">
          <slot name="content" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import MIcon from 'components/Design/MIcon';
  import bulletIcon from 'assets/icons/bullet-arrow.svg';
  import smoothCrossIcon from 'assets/icons/smooth-cross.svg';

  export default {
    name: 'Accordion',
    components: {
      MIcon
    },
    props: {
      isOpen: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      headerColor: {
        type: String,
        required: true
      },
      contentColor: {
        type: String,
        default: 'rgba(255, 255, 255, 1)'
      },
      needCloseIcon: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        bulletIcon,
        smoothCrossIcon,
        isOpenInternal: this.isOpen
      };
    },
    computed: {
      cssVars () {
        return {
          '--header-color': this.headerColor,
          '--content-color': this.contentColor
        };
      }
    },
    watch: {
      isOpen (newValue) {
        this.isOpenInternal = newValue;
      },
      isOpenInternal (newValue) {
        this.$emit('toggle', newValue);
      }
    },
    methods: {
      toggle () {
        if (this.disabled) {
          return;
        }
        this.isOpenInternal = !this.isOpenInternal;
      },
      onEnter (el) {
        el.style.height = '0';
        el.style.overflow = 'hidden';

        el.offsetHeight;
        el.style.height = `${el.scrollHeight}px`;
      },
      onAfterEnter (el) {
        el.style.height = 'auto';
        el.style.overflow = '';
      },
      onLeave (el) {
        el.style.height = `${el.scrollHeight}px`;
        el.style.overflow = 'hidden';

        el.offsetHeight;
        el.style.height = '0';
      },
      onAfterLeave (el) {
        el.style.height = '';
        el.style.overflow = '';
      },
      handleClose () {
        this.$emit('close');
      }
    }
  };
</script>

<style scoped>
  .accordion {
    width: 100%;
  }

  .accordion__header {
    cursor: pointer;

    display: flex;

    justify-content: space-between;
    align-items: center;

    box-sizing: border-box;
    width: 100%;

    padding: 7px 16px;

    background-color: var(--header-color);

    border-radius: 5px;

    transition: border-radius 150ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .accordion__header:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .accordion__icon {
    color: rgba(79, 79, 79, 1);

    flex-shrink: 0;

    width: 18px;
    height: 18px;

    margin-left: 8px;

    transform: rotate(90deg);

    transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .accordion.accordion--open {
    & .accordion__icon {
      transform: rotate(270deg);
    }

    & .accordion__header {
      border-radius: 5px 5px 0 0;
    }
  }

  .accordion__header-content {
    display: flex;
    align-items: center;
  }

  .accordion__cross-icon {
    color: rgba(168, 168, 168, 1);

    flex-shrink: 0;

    width: 18px;
    height: 18px;

    opacity: 1;

    transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      opacity: 0.75;
    }
  }

  .accordion__content {
    background-color: var(--content-color);
    box-shadow: 0 2px 12px 0 rgba(71, 71, 71, 0.1);

    border-radius: 0 0 5px 5px;
  }

  .accordion__content-inner {
    padding: 12px 16px;
  }

  .accordion-content-enter-active,
  .accordion-content-leave-active {
    transition: height 300ms cubic-bezier(0.4, 0, 0.2, 1);
  }
</style>
